import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import EntityForm from "@/components/EntityForm";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, Percent, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function VatRates() {
  const [items, setItems] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/accounting/tva-rates").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/accounting/accounts").then((r) => setAccounts(r.data)).catch(() => {}); }, []);

  const acctOptions = accounts.map((a) => ({ value: a.numero, label: `${a.numero} — ${a.libelle}` }));
  const schema = [
    { name: "libelle", label: "Libellé", required: true },
    { name: "taux", label: "Taux (%)", type: "number", required: true },
    { name: "compte_collecte", label: "Compte TVA collectée", type: "select", options: acctOptions },
    { name: "compte_deductible", label: "Compte TVA déductible", type: "select", options: acctOptions },
  ];

  const save = async (form) => {
    const payload = { ...form, taux: Number(form.taux) || 0 };
    try {
      if (editing) await api.put(`/accounting/tva-rates/${editing.id}`, payload);
      else await api.post("/accounting/tva-rates", payload);
      toast.success(editing ? "Taux mis à jour" : "Taux créé"); load();
    } catch (e) { toast.error(apiErr(e)); throw e; }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer ce taux ?")) return; await api.delete(`/accounting/tva-rates/${id}`); toast.success("Supprimé"); load(); };

  return (
    <div>
      <PageHeader title="TVA & taux" subtitle="Taux de TVA et comptes de taxe associés">
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-tva" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouveau taux</Button>
      </PageHeader>
      {items === null ? <Loader /> : items.length === 0 ? (
        <EmptyState icon={Percent} title="Aucun taux" subtitle="Ajoutez un taux de TVA." />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Libellé</TableHead><TableHead>Taux</TableHead><TableHead>TVA collectée</TableHead><TableHead>TVA déductible</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((t) => (
                <TableRow key={t.id} data-testid={`tva-row-${t.id}`} className="hover:bg-slate-50">
                  <TableCell className="font-semibold">{t.libelle}</TableCell>
                  <TableCell className="font-mono">{t.taux} %</TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{t.compte_collecte || "—"}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{t.compte_deductible || "—"}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`tva-menu-${t.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => { setEditing(t); setOpen(true); }}><Pencil className="h-4 w-4 mr-2" />Modifier</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => remove(t.id)} className="text-rose-600"><Trash2 className="h-4 w-4 mr-2" />Supprimer</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div></div>
      )}
      <EntityForm open={open} onOpenChange={setOpen} title={editing ? "Modifier le taux" : "Nouveau taux de TVA"} schema={schema} initial={editing || { taux: 18 }} onSubmit={save} />
    </div>
  );
}
